import React, {useCallback, useEffect, useRef, useState} from 'react';
import {Grid, Typography, Box, Checkbox} from '@mui/material';
import {FixedSizeList, ListChildComponentProps} from 'react-window';
import {t} from 'i18next';
import styles from '../../../styles/components/bulk-tagging/style.module.scss';
import AurSearchBox from '../../common/aur-search-box/AurSearchBox';
import TagSelectListItem from './AurTagSelectListItem';
import {
  NEW_TAG_CONTAINER_HEIGHT,
  NEW_TAG_ERROR_CONTAINER_HEIGHT,
} from './AddNewTag';
import {ITag} from '../../pages/field-management/custom-field/DraggableTag';

const SCROLL_BAR_STYLE_CLASS = 'aur-item-container-scroll'; // Scrollbar selector class

type AurTagSelectListProps = {
  items: ITag[]; // All options/items
  selectedTags: ITag[]; // All selected tags
  selectAllLabel?: string; // Select all checkbox label
  itemHeight?: number; // Item height
  tagAction?: string;
  onSelectionChange: (
    ids: ITag[],
    taggingType: string,
    targetItem?: ITag,
    allSelect?: string
  ) => void; // Called after items/options commit/add
  renderSubHeader?: () => React.ReactElement;
  isError?: boolean;
  height?: number;
  showSearchLabel?: boolean;
  searchPlaceHolder?: string;
};

const AurTagSelectList = (props: AurTagSelectListProps) => {
  const {
    items,
    selectAllLabel,
    itemHeight = 40,
    onSelectionChange,
    renderSubHeader,
    tagAction,
    selectedTags,
    isError,
    height,
    showSearchLabel = true,
    searchPlaceHolder,
  } = props;

  const [searchTerm, setSearchTerm] = useState('');
  const [listHeight, setlistHeight] = useState<number>(0);
  const getAllSelectState = (): 'check' | 'partialCheck' | '' => {
    if (selectedTags.length === 0) return '';
    if (selectedTags.length === items.length) return 'check';
    return 'partialCheck';
  };

  const [allSelect, setAllSelect] = useState<'check' | 'partialCheck' | ''>(
    getAllSelectState()
  );

  useEffect(() => {
    setAllSelect(getAllSelectState());
  }, [items.length, selectedTags.length]);

  // Called for tag selected/unselected
  const selectionChangeHandler = useCallback(
    (data: ITag) => {
      const targetItem = selectedTags.find(
        (item) => item.tagValueId == data.tagValueId
      );
      if (targetItem) {
        onSelectionChange(
          selectedTags.filter((tag) => tag.tagValueId != targetItem.tagValueId),
          'untag',
          targetItem
        );
      } else {
        onSelectionChange([...selectedTags, data], 'tag', targetItem);
      }
    },
    [onSelectionChange, selectedTags]
  );

  const selectAll = () => {
    setAllSelect((pv) => {
      if (pv == 'check' || pv == 'partialCheck') {
        onSelectionChange([], 'untag', undefined, 'unCheck');
        return '';
      } else {
        onSelectionChange(items, 'tag', undefined, 'check');
        return 'check';
      }
    });
  };

  // Called for "Select All" checkbox button
  const selectAllHandler = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      event.stopPropagation();
      selectAll();
    },
    [items, onSelectionChange]
  );

  const selectAllRef = useRef();

  useEffect(() => {
    if (selectAllRef.current) {
      const {offsetTop, offsetHeight} = selectAllRef.current;

      let dynamicOffset = 0;

      if (tagAction === 'tag') {
        dynamicOffset = NEW_TAG_CONTAINER_HEIGHT;
        if (isError) {
          dynamicOffset += NEW_TAG_ERROR_CONTAINER_HEIGHT;
        }
      }

      setlistHeight(
        window.innerHeight - offsetHeight - offsetTop - 8 - dynamicOffset
      );
    }
  }, [isError, tagAction]);

  const searchItems = (keyword: string) => {
    if (keyword.length > 0) {
      setSearchTerm(keyword);
    } else {
      //Reset search state
      setSearchTerm('');
    }
  };

  const renderSelectItem = useCallback(
    (props: ListChildComponentProps) => {
      const {index, style} = props;

      let component = <></>;
      const filteredTagData = items.filter((tag) =>
        tag.tagValue.toLowerCase().includes(searchTerm.toLowerCase())
      );
      const item = filteredTagData[index];

      if (item) {
        component = (
          <ul style={{...style, listStyle: 'none', margin: 0, padding: 0}}>
            <TagSelectListItem
              key={item.tagValueId}
              itemData={item}
              onItemClick={selectionChangeHandler}
              isSelected={selectedTags.some(
                (tag) => tag.tagValueId == item.tagValueId
              )}
              isLastItem={index === filteredTagData.length - 1}
            />
          </ul>
        );
      }

      return component;
    },
    [searchTerm, selectedTags, selectionChangeHandler, items]
  );

  const renderlistHeader = useCallback(() => {
    return (
      <Box className="select-all-container" ref={selectAllRef}>
        <Grid
          container
          direction={'row'}
          justifyContent={'space-between'}
          alignItems={'center'}
        >
          <Grid item>
            <Box
              onClick={selectAllHandler}
              sx={{
                height: 42,
                alignItems: 'center',
              }}
            >
              <Checkbox
                className={styles.aurSelectItemCheckBox}
                checked={allSelect == 'check'}
                indeterminate={allSelect == 'partialCheck'}
                inputProps={{
                  'aria-label': t('common.selectAll'),
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    selectAll();
                  }
                }}
                checkedIcon={
                  <img
                    src={'/assests/images/checked_checkbox.svg'}
                    alt="All tags selected"
                  />
                }
                indeterminateIcon={
                  <img
                    src={'/assests/images/indeterminate_checkbox.svg'}
                    alt="Some tags selected"
                  />
                }
              ></Checkbox>
              <Typography
                className={`typo-text body3_size ${styles.aurAllSelectLabel} `}
              >
                {selectAllLabel}
              </Typography>
            </Box>
          </Grid>
          {renderSubHeader?.()}
        </Grid>
      </Box>
    );
  }, [selectAllHandler, allSelect, selectAllLabel, renderSubHeader]);

  const renderTagList = useCallback(() => {
    const filteredTagData = items.filter((tag) =>
      tag.tagValue.toLowerCase().includes(searchTerm.toLowerCase())
    );
    return (
      <Box className="tag-list">
        <FixedSizeList
          height={height ?? listHeight}
          itemCount={filteredTagData.length}
          itemSize={itemHeight}
          width={'100%'}
          className={`${SCROLL_BAR_STYLE_CLASS} ${styles.aurItemContainerScrollbar} `}
        >
          {renderSelectItem}
        </FixedSizeList>
      </Box>
    );
  }, [items, height, listHeight, itemHeight, renderSelectItem, searchTerm]);

  const serachBoxProps = showSearchLabel
    ? {placeHolder: searchPlaceHolder}
    : {placeHolder: searchPlaceHolder, label: ''};

  return (
    <>
      <Box className="tag-list-header">
        <Box className="select-search-box">
          <AurSearchBox
            debounceTimeout={100}
            onSearch={searchItems}
            hintText="Enter your search term"
            {...serachBoxProps}
          />
        </Box>
        {renderlistHeader()}
      </Box>
      {renderTagList()}
    </>
  );
};

export default AurTagSelectList;
